import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { WebSocketProvider } from "@/contexts/WebSocketContext";
import { Toaster } from "@/components/ui/sonner";

// Pages
import SplashScreen from "@/pages/SplashScreen";
import OnboardingPage from "@/pages/OnboardingPage";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import VerificationPage from "@/pages/VerificationPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ProfileCompletePage from "@/pages/ProfileCompletePage";
import MatchesPage from "@/pages/MatchesPage";
import ConversationsPage from "@/pages/ConversationsPage";
import GuidedChatPage from "@/pages/GuidedChatPage";
import ProfilePage from "@/pages/ProfilePage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";
import LikesPage from "@/pages/LikesPage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import ReferralPage from "@/pages/ReferralPage";
import InsightsPage from "@/pages/InsightsPage";
import AdvancedFiltersPage from "@/pages/AdvancedFiltersPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import CookiePolicyPage from "@/pages/CookiePolicyPage";
import ContactPage from "@/pages/ContactPage";
import SafetyTipsPage from "@/pages/SafetyTipsPage";
import SafetyStandardsPage from "@/pages/SafetyStandardsPage";
import PromoLandingPage from "@/pages/PromoLandingPage";
import RedesignPreviewLanding from "@/pages/RedesignPreviewLanding";
import RedesignPreviewApp from "@/pages/RedesignPreviewApp";
import LandingPageV2 from "@/pages/LandingPageV2";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminReports from "@/pages/admin/AdminReports";
import AdminCommunity from "@/pages/admin/AdminCommunity";
import AdminTransactions from "@/pages/admin/AdminTransactions";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminEvents from "@/pages/admin/AdminEvents";
import AdminVerifications from "@/pages/admin/AdminVerifications";
import AdminSupport from "@/pages/admin/AdminSupport";
import AdminWaitlist from "@/pages/admin/AdminWaitlist";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminMessaging from "@/pages/admin/AdminMessaging";
import AdminDocs from "@/pages/admin/AdminDocs";
import AdminTeam from "@/pages/admin/AdminTeam";
import AdminInfrastructure from "@/pages/admin/AdminInfrastructure";
import AdminSecurity from "@/pages/admin/AdminSecurity";
import AdminTesterInvites from "@/pages/admin/AdminTesterInvites";
import AdminMatchmaker from "@/pages/admin/AdminMatchmaker";
import AdminBetaTesters from "@/pages/admin/AdminBetaTesters";
import AdminAuditLog from "@/pages/admin/AdminAuditLog";
import AdminSetupPage from "@/pages/AdminSetupPage";
import MatchDemoPage from "@/pages/MatchDemoPage";
import SettingsPage from "@/pages/SettingsPage";
import BlockedUsersPage from "@/pages/BlockedUsersPage";
import SupportPage from "@/pages/SupportPage";
import AuthCallback from "@/pages/AuthCallback";
import FounderPage from "@/pages/FounderPage";
import EbookPage from "@/pages/EbookPage";
import DateCheckinPage from "@/pages/DateCheckinPage";
import GiftShopPage from "@/pages/GiftShopPage";
import BetaSignupPage from "@/pages/BetaSignupPage";

const ShopRedirect = () => {
  React.useEffect(() => {
    window.location.replace("https://shop.recoverymatch.com");
  }, []);
  return null;
};
import AdminLoginPage from "@/pages/AdminLoginPage";
import FAQPage from "@/pages/FAQPage";
import HowCoinsWorkPage from "@/pages/HowCoinsWorkPage";
import ProfileEditPage from "@/pages/ProfileEditPage";
import DeleteAccountPage from "@/pages/DeleteAccountPage";
import AccountDeletionPage from "@/pages/AccountDeletionPage";
import EULAPage from "@/pages/EULAPage";
import AppShell from "@/components/AppShell";

// Check if user has completed onboarding
const hasCompletedOnboarding = () => {
  return localStorage.getItem("onboarding_complete") === "true";
};

// Protected Route Component
const ProtectedRoute = ({ children, requireOnboarding = true }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1C3A7A] to-[#0f2347] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#FAB710] border-t-transparent"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Check if user needs to complete new hybrid onboarding
  if (requireOnboarding && !user.profile_complete) {
    return <Navigate to="/onboarding" replace />;
  }
  
  return children;
};


// Member Route: auth + desktop sidebar shell
const MemberRoute = ({ children }) => (
  <ProtectedRoute><AppShell>{children}</AppShell></ProtectedRoute>
);

// Admin Route Component - permission-aware (M2 P2); backend 403 remains the security boundary
import { AdminAuthProvider, useAdminAuth } from "./contexts/AdminAuthContext";
import AdminForbidden from "@/pages/admin/AdminForbidden";
import AdminChangePassword from "@/components/admin/AdminChangePassword";
import AdminLayout from "@/components/AdminLayout";

const AdminRouteInner = ({ children, requiredPerm }) => {
  const { admin, loading, hasPerm } = useAdminAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#FAB710] border-t-transparent"></div>
      </div>
    );
  }
  if (!admin) {
    return <Navigate to="/admin/login" replace />;
  }
  if (requiredPerm && !hasPerm(requiredPerm)) {
    return <AdminLayout><AdminForbidden /></AdminLayout>;
  }
  if (admin.must_change_password) {
    return (
      <AdminLayout>
        <div className="max-w-xl mx-auto py-10 px-4" data-testid="forced-password-change">
          <h1 className="text-xl font-bold text-[#1C3A7A] mb-2">Password change required</h1>
          <p className="text-gray-600 mb-6">You must change your password before using the admin panel.</p>
          <AdminChangePassword />
        </div>
      </AdminLayout>
    );
  }
  return children;
};

const AdminRoute = ({ children, requiredPerm }) => (
  <AdminAuthProvider>
    <AdminRouteInner requiredPerm={requiredPerm}>{children}</AdminRouteInner>
  </AdminAuthProvider>
);

function AppRoutes() {
  const { user } = useAuth();
  const location = useLocation();
  const onboardingDone = hasCompletedOnboarding();
  
  // Check for OAuth callback with session_id in URL fragment
  // This must be checked synchronously during render to prevent race conditions
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Landing Page - Main entry for non-authenticated users */}
      <Route 
        path="/" 
        element={
          user 
            ? <Navigate to="/matches" replace /> 
            : <LandingPageV2 />
        } 
      />
      
      {/* Onboarding & Splash (alternative flows) */}
      <Route path="/onboarding" element={
        user ? (
          user.profile_complete ? <Navigate to="/matches" replace /> : <OnboardingPage />
        ) : <Navigate to="/login" replace />
      } />
      <Route path="/splash" element={user ? <Navigate to="/matches" replace /> : <SplashScreen />} />
      
      {/* Auth Routes */}
      <Route path="/login" element={user ? <Navigate to={user.is_admin ? "/admin" : "/matches"} replace /> : <LoginPage />} />
      <Route path="/register" element={user ? <Navigate to="/matches" replace /> : <RegisterPage />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/matches" replace /> : <ForgotPasswordPage />} />
      <Route path="/verification" element={user ? <Navigate to="/matches" replace /> : <VerificationPage />} />
      <Route path="/reset-password" element={user ? <Navigate to="/matches" replace /> : <ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Legal Pages (public) */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/eula" element={<EULAPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/cookies" element={<CookiePolicyPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/safety" element={<SafetyTipsPage />} />
      <Route path="/safety-standards" element={<SafetyStandardsPage />} />
      <Route path="/promo/:code" element={<PromoLandingPage />} />
      <Route path="/promo" element={<PromoLandingPage />} />
      <Route path="/redesign/landing" element={<RedesignPreviewLanding />} />
      <Route path="/redesign/app" element={<RedesignPreviewApp />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/shop" element={<ShopRedirect />} />
      <Route path="/date-checkin" element={<ProtectedRoute><DateCheckinPage /></ProtectedRoute>} />
      
      {/* Protected Routes */}
      <Route path="/profile-complete" element={<ProtectedRoute><ProfileCompletePage /></ProtectedRoute>} />
      <Route path="/matches" element={<MemberRoute><MatchesPage /></MemberRoute>} />
      <Route path="/messages" element={<MemberRoute><ConversationsPage /></MemberRoute>} />
      <Route path="/chat/:matchId" element={<MemberRoute><GuidedChatPage /></MemberRoute>} />
      <Route path="/profile" element={<MemberRoute><ProfilePage /></MemberRoute>} />
      <Route path="/profile/edit" element={<MemberRoute><ProfileEditPage /></MemberRoute>} />
      <Route path="/subscription" element={<MemberRoute><SubscriptionPage /></MemberRoute>} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/cancel" element={<PaymentCancelPage />} />
      <Route path="/likes" element={<MemberRoute><LikesPage /></MemberRoute>} />
      <Route path="/leaderboard" element={<MemberRoute><LeaderboardPage /></MemberRoute>} />
      <Route path="/referral" element={<MemberRoute><ReferralPage /></MemberRoute>} />
      <Route path="/insights" element={<MemberRoute><InsightsPage /></MemberRoute>} />
      <Route path="/filters" element={<MemberRoute><AdvancedFiltersPage /></MemberRoute>} />
      <Route path="/advanced-filters" element={<MemberRoute><AdvancedFiltersPage /></MemberRoute>} />
      <Route path="/verification" element={<ProtectedRoute><VerificationPage /></ProtectedRoute>} />
      <Route path="/settings" element={<MemberRoute><SettingsPage /></MemberRoute>} />
      <Route path="/settings/blocked" element={<MemberRoute><BlockedUsersPage /></MemberRoute>} />
      
      {/* Legacy route redirect */}
      <Route path="/discover" element={<Navigate to="/matches" replace />} />
      <Route path="/settings/verification" element={<ProtectedRoute><VerificationPage /></ProtectedRoute>} />
      <Route path="/support" element={<MemberRoute><SupportPage /></MemberRoute>} />
      <Route path="/gift-shop" element={<MemberRoute><GiftShopPage /></MemberRoute>} />
      <Route path="/how-coins-work" element={<MemberRoute><HowCoinsWorkPage /></MemberRoute>} />
      
      {/* Admin Routes (requiredPerm = UX gate; backend RBAC is the boundary) */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin/setup" element={<AdminSetupPage />} />
      <Route path="/admin" element={<AdminRoute requiredPerm="analytics.read"><AdminDashboard /></AdminRoute>} />
      <Route path="/admin/users" element={<AdminRoute requiredPerm="members.read"><AdminUsers /></AdminRoute>} />
      <Route path="/admin/reports" element={<AdminRoute requiredPerm="moderation.read"><AdminReports /></AdminRoute>} />
      <Route path="/admin/community" element={<AdminRoute requiredPerm="moderation.read"><AdminCommunity /></AdminRoute>} />
      <Route path="/admin/transactions" element={<AdminRoute requiredPerm="finance.read"><AdminTransactions /></AdminRoute>} />
      <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
      <Route path="/admin/events" element={<AdminRoute requiredPerm="analytics.read"><AdminEvents /></AdminRoute>} />
      <Route path="/admin/verifications" element={<AdminRoute requiredPerm="verification.update"><AdminVerifications /></AdminRoute>} />
      <Route path="/admin/support" element={<AdminRoute requiredPerm="support.read"><AdminSupport /></AdminRoute>} />
      <Route path="/admin/waitlist" element={<AdminRoute requiredPerm="members.read"><AdminWaitlist /></AdminRoute>} />
      <Route path="/admin/subscriptions" element={<AdminRoute requiredPerm="finance.read"><AdminSubscriptions /></AdminRoute>} />
      <Route path="/admin/messaging" element={<AdminRoute requiredPerm="analytics.read"><AdminMessaging /></AdminRoute>} />
      <Route path="/admin/docs" element={<AdminRoute requiredPerm="system.debug"><AdminDocs /></AdminRoute>} />
      <Route path="/admin/team" element={<AdminRoute requiredPerm="team.manage"><AdminTeam /></AdminRoute>} />
      <Route path="/admin/infrastructure" element={<AdminRoute requiredPerm="system.debug"><AdminLayout><AdminInfrastructure /></AdminLayout></AdminRoute>} />
      <Route path="/admin/security" element={<AdminRoute requiredPerm="security.admin"><AdminSecurity /></AdminRoute>} />
      <Route path="/admin/tester-invites" element={<AdminRoute requiredPerm="comms.send"><AdminLayout><AdminTesterInvites /></AdminLayout></AdminRoute>} />
      <Route path="/admin/matchmaker" element={<AdminRoute requiredPerm="matchmaking.tools"><AdminLayout><AdminMatchmaker /></AdminLayout></AdminRoute>} />
      <Route path="/admin/beta" element={<AdminRoute requiredPerm="members.read"><AdminBetaTesters /></AdminRoute>} />
      <Route path="/admin/audit" element={<AdminRoute requiredPerm="audit.read"><AdminAuditLog /></AdminRoute>} />
      
      {/* Demo Routes */}
      <Route path="/demo/match" element={<MatchDemoPage />} />
      
      {/* Public Pages */}
      <Route path="/founder" element={<FounderPage />} />
      <Route path="/guide" element={<EbookPage />} />
      <Route path="/ebook" element={<EbookPage />} />
      <Route path="/beta" element={<BetaSignupPage />} />
      <Route path="/delete-account" element={<DeleteAccountPage />} />
      
      {/* In-App Account Deletion (requires auth) */}
      <Route path="/account/delete" element={
        <ProtectedRoute>
          <AccountDeletionPage />
        </ProtectedRoute>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <WebSocketProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-center" richColors />
          </BrowserRouter>
        </WebSocketProvider>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
